import classNames from "classnames";
import { useState } from "react";
import { Props, MarketsOption } from "./types";
import DataContainer from "../../services/DataContainer";
import { ContactDealerFormSettings } from "../../enum/enums";
import { generateAccessibleId } from "../../utils/accessibility";

export const Dropdown = ({
  options,
  label,
  onChangeEvent,
  name,
  isRequired,
  selectedOption,
  toggleTextCenter,
  variant = "primary",
  labelTransitions = true,
  ariaLabel,
  ariaDescribedBy,
  ariaInvalid,
  ariaRequired,
  errorMessage,
  placeholder,
}: Props) => {  
  
  const [toggleClassLabel, setToggleClassLabel] = useState(false);
  
  // Generate unique IDs for accessibility
  const selectId = generateAccessibleId("select", name || "dropdown");
  const errorId = generateAccessibleId("error", name || "dropdown");
  const labelId = generateAccessibleId("label", name || "dropdown");

  // Determine if dropdown has error state
  const hasError = !selectedOption && isRequired;
  const finalAriaInvalid = ariaInvalid !== undefined ? ariaInvalid : hasError;
  const finalAriaRequired = ariaRequired !== undefined ? ariaRequired : isRequired;

  return (
    <div
      className={classNames("cursor-pointer flex flex-col relative py-4", {
        "bg-primary": variant === "primary",
        "bg-transparent": variant === "secondary",
      })}
    >
      <label
        id={labelId}
        htmlFor={selectId}
        className={classNames(
          "h-6 text-grey-dark",
          { "text-center": toggleTextCenter },
          { "text-xl text-blue uppercase -translate-y-spacing-h-sm": (toggleClassLabel && labelTransitions) }
        )}
      >
        {label}
        {isRequired && <span className="text-red ml-1" aria-label="required">*</span>}
      </label>
      <select
        id={selectId}
        name={name}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy || (hasError ? errorId : undefined)}
        aria-invalid={finalAriaInvalid}
        aria-required={finalAriaRequired}
        onChange={onChangeEvent}
        className={classNames(
          `${!name ? "py-2.5" : "py-0"} px-1 bg-transparent border-b border-black focus:border-none focus-visible:outline-none focus:ring-2 focus:rounded-xl focus:ring-blue-500`,
          {"border-red focus:ring-red": hasError}
        )}
        onFocus={() => setToggleClassLabel(true)}
        onBlur={(e) => !e.currentTarget.value && setToggleClassLabel(false)}
        value={selectedOption}
      >
        {placeholder && <option value="" disabled>{placeholder}</option>}
        {options.map((option: MarketsOption, index: number) => {
          if (option.locale === "en") return null;

          const optionValue = option.locale ?? option.option ?? option.id;
          const optionLabel = option.language ?? option.option ?? option.id;

          return (
            <option
              className={classNames({ "text-center": toggleTextCenter })}
              key={option.id || index}
              value={optionValue}
              id={option.id}
            >
              {optionLabel}
            </option>
          );
        })}
      </select>
      {hasError && errorMessage && (
        <span 
          id={errorId}
          className="text-red text-xl h-4"
          role="alert"
          aria-live="polite"
        >
          {errorMessage}
        </span>
      )}
      {(isRequired && selectedOption === "") && (
        <span className="text-red text-xl h-4">{DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM,`contact_dealer_${name === "salesforceSegment" ? "segment" : name}_required_text`)}</span>
      )}
    </div>
  );
};
